import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  makeStyles,
  IconButton,
  Zoom
} from "@material-ui/core";
import { Receipt, Close, InfoOutlined } from "@material-ui/icons";
import { useTranslation } from 'react-i18next';


const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 32,
    background: "#0f172a",
    color: "#ffffff",
    boxShadow: "0 40px 100px rgba(0,0,0,0.6)",
    margin: theme.spacing(2),
    position: "relative",
    border: "1px solid rgba(255, 255, 255, 0.08)",
  },
  closeIconButton: {
    position: "absolute",
    right: theme.spacing(3),
    top: theme.spacing(3),
    color: "#94a3b8",
    zIndex: 10,
    background: "rgba(255, 255, 255, 0.05)",
    "&:hover": {
      color: "#ffffff",
      background: "rgba(239, 68, 68, 0.2)",
      transform: "rotate(90deg)",
    },
  },
  header: {
    padding: theme.spacing(6, 4, 3, 4),
    textAlign: "center",
    "& h2": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(2),
      fontSize: "1.8rem",
      fontWeight: 900,
      color: "#f8fafc",
    },
  },
  summaryBanner: {
    margin: theme.spacing(0, 4, 3, 4),
    padding: theme.spacing(3, 5),
    borderRadius: 24,
    background: "linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)",
    border: "1px solid rgba(99, 102, 241, 0.3)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  vatRateBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    background: "rgba(99, 102, 241, 0.15)",
    color: "#a5b4fc",
    padding: "10px 18px",
    borderRadius: "14px",
    fontSize: "0.9rem",
    fontWeight: 800,
    border: "1px solid rgba(99, 102, 241, 0.4)",
    marginTop: 12,
  },
  scrollContent: {
    padding: "0 32px 32px 32px !important",
    overflowY: "auto",
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-thumb": { 
      background: "rgba(99, 102, 241, 0.3)", 
      borderRadius: "10px" 
    },
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
    gap: "24px",
  },
  
  card: {
    background: "#1e293b",
    borderRadius: 24,
    padding: theme.spacing(3.5),
    border: "1px solid rgba(255, 255, 255, 0.05)",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)", 
    position: "relative",
    "&:hover": {
      transform: "translateY(-8px) scale(1.02)", 
      borderColor: "#6366f1", 
      boxShadow: "0 20px 40px rgba(0,0,0,0.4), 0 0 20px rgba(99, 102, 241, 0.2)", 
      background: "#232e42",
    },
  },
  subListWrapper: {
    maxHeight: "180px", 
    overflowY: "auto",
    paddingRight: "8px",
    marginBottom: theme.spacing(2),
    "&::-webkit-scrollbar": { width: "4px" },
    "&::-webkit-scrollbar-thumb": { 
      background: "rgba(255, 255, 255, 0.1)", 
      borderRadius: "10px" 
    },
  },
  subItemRow: {
    background: "rgba(255, 255, 255, 0.03)",
    padding: theme.spacing(2),
    borderRadius: 16,
    marginBottom: theme.spacing(1.5),
    border: "1px solid rgba(255, 255, 255, 0.02)",
  },
  vatTagLarge: {
    fontSize: 11,
    background: "#6366f1",
    color: "#ffffff",
    padding: "2px 8px",
    borderRadius: 6,
    marginLeft: 8,
    fontWeight: 900,
  }
}));

const PricePreviewDialog = ({ open, onClose, data = [], isApiSuccess, hasBusinessError, errorMessage, tenantId }) => {
  const classes = useStyles();
  const { t } = useTranslation('overview');

  const isRussia = tenantId === 118;
  const VAT_PERCENT = isRussia ? 22 : 0;
  const VAT_RATE = isRussia ? 1.22 : 1.0;

  const isError = !isApiSuccess || hasBusinessError;

  const formatCurrency = (val, curr = "RUB") => 
    val.toLocaleString("ru-RU", { style: "currency", currency: curr });

  const { processedItems, totalNet, totalWithVat } = useMemo(() => {
    let netSum = 0;
    const map = {};
    data.forEach((item) => {
      const [code, substituted, priceStr, currency] = (item.MaterialCode || "").split("|");
      const price = parseFloat(priceStr || "0");
      const curr = currency || "RUB";
      if (!map[code]) map[code] = { code, currency: curr, subs: [], netTotal: 0 };
      if (!map[code].subs.find(x => x.sub === substituted)) {
        map[code].subs.push({ sub: substituted, net: price, vat: price * VAT_RATE });
        map[code].netTotal += price;
        netSum += price;
      }
    });
    return { processedItems: Object.values(map), totalNet: netSum, totalWithVat: netSum * VAT_RATE };
  }, [data, VAT_RATE]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth TransitionComponent={Zoom} classes={{ paper: classes.dialogPaper }}>
      <IconButton className={classes.closeIconButton} onClick={onClose}><Close /></IconButton>

      <DialogTitle disableTypography className={classes.header}>
        <Typography variant="h2">
          <Receipt style={{ fontSize: 32, color: "#6366f1" }} />
          {t('PricingDetails')}
        </Typography>
      </DialogTitle>

      {isError ? (
        <Box
          style={{
            margin: "0 32px 24px",
            padding: 24,
            borderRadius: 20,
            background: "rgba(245, 158, 11, 0.15)", // amber
            border: "1px solid rgba(245, 158, 11, 0.5)",
            display: "flex",
            gap: 16,
            alignItems: "center"
          }}
        >
          <InfoOutlined style={{ color: "#fbbf24", fontSize: 30 }} />
          <Box>
            <Typography style={{ color: "#fde68a", fontWeight: 600 }}>
              {errorMessage ||  t('PriceSimulationFailed')}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box className={classes.summaryBanner}>
          <Box>
            <Typography style={{ color: "#94a3b8", fontWeight: 700 }}>
              {processedItems.length} {t('ItemsCalculated')}
            </Typography>
          </Box>
          <Box textAlign="right">
            {isRussia && (
              <Typography style={{ color: "#818cf8", fontSize: "0.85rem", fontWeight: 800 }}>
                {t('Net')} : {formatCurrency(totalNet)}
              </Typography>
            )}
            <Typography style={{ fontSize: "2.8rem", fontWeight: 950, color: "#ffffff", lineHeight: 1 }}>
              {formatCurrency(totalWithVat)}
            </Typography>
            <Typography style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>
              {isRussia ? t('TotalWithVat') : t('TOTAL')}
            </Typography>
          </Box>
        </Box>
      )}

      <DialogContent className={classes.scrollContent}>
        <Box className={classes.gridContainer}>
          {processedItems.map((item, idx) => (
            <Box key={idx} className={classes.card}>
              <Typography style={{ fontSize: 10, color: "#6366f1", fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>{t('Material')}</Typography>
              <Typography style={{ color: "#ffffff", fontWeight: 800, fontSize: "1.2rem", marginBottom: 20 }}>{item.code}</Typography>

              <Box className={classes.subListWrapper}>
                {item.subs.filter(s => s.sub !== item.code).map((s, i) => (
                  <Box key={i} className={classes.subItemRow}>
                    <Typography style={{ fontSize: 11, color: "#94a3b8", marginBottom: 6 }}>{t('Sub')}: {s.sub}</Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{formatCurrency(s.net, item.currency)}</Typography>
                      <Box display="flex" alignItems="center">
                        <Typography style={{ fontSize: 15, fontWeight: 900, color: "#f8fafc" }}>{formatCurrency(s.vat, item.currency)}</Typography>
                        {isRussia && <span className={classes.vatTagLarge}>%{VAT_PERCENT}</span>}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box style={{ marginTop: "auto", borderTop: "2px solid rgba(255,255,255,0.05)", display: "flex", justifyContent: "space-between", alignItems: "flex-end", paddingTop: 16 }}>
                <Box>
                  {isRussia && (
                    <>
                      <Typography style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>{t('NET')}</Typography>
                      <Typography style={{ color: "#94a3b8", fontWeight: 800 }}>{formatCurrency(item.netTotal, item.currency)}</Typography>
                    </>
                  )}
                </Box>
                <Box textAlign="right">
                  <Typography style={{ fontSize: 10, color: "#818cf8", fontWeight: 800 }}>{t('TOTAL')} {isRussia && <span style={{ color: "#6366f1" }}>%{VAT_PERCENT}</span>}</Typography>
                  <Typography style={{ fontSize: "1.7rem", fontWeight: 950, color: "#fff" }}>{formatCurrency(item.netTotal * VAT_RATE, item.currency)}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PricePreviewDialog;